import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from '../utils/toast';
import { FiPlus, FiSearch, FiX, FiEye, FiXCircle, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import { fmtFecha, localDate } from '../utils/format';
import '../styles/Compras.css';
const fmt = (n) => parseFloat(n || 0).toFixed(2);
const today = () => localDate();

const newItem = () => ({
  _id: Date.now(),
  producto_id: null,
  producto_nombre: '',
  unidad: '',
  cantidad: '',
  precio_unitario: '',
  descuento: 0,
  igv: 0,
  subtotal: 0,
});

const calcItem = (it) => {
  const qty   = parseFloat(it.cantidad)        || 0;
  const price = parseFloat(it.precio_unitario) || 0;
  const desc  = parseFloat(it.descuento)       || 0;
  const igv   = parseFloat(it.igv)             || 0;
  return parseFloat(((qty * price - desc) + igv).toFixed(4));
};

export default function ComprasPage() {
  /* ── listado ── */
  const [rows,       setRows]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  /* ── filtros ── */
  const [search,     setSearch]     = useState('');
  const [fDesde,     setFDesde]     = useState('');
  const [fHasta,     setFHasta]     = useState('');
  const [fEstado,    setFEstado]    = useState('');
  const [fProv,      setFProv]      = useState('');

  /* ── modales ── */
  const [modalNueva, setModalNueva]   = useState(false);
  const [modalDet,   setModalDet]     = useState(null);
  const [confirmId,  setConfirmId]    = useState(null);
  const [saving,     setSaving]       = useState(false);
  const [modalError, setModalError]   = useState('');

  /* ── form nueva compra ── */
  const [proveedores,    setProveedores]    = useState([]);
  const [provSearch,     setProvSearch]     = useState('');
  const [provSugest,     setProvSugest]     = useState([]);
  const [provSel,        setProvSel]        = useState(null);
  const [tipoDoc,        setTipoDoc]        = useState('FACTURA');
  const [serie,          setSerie]          = useState('');
  const [nroDoc,         setNroDoc]         = useState('');
  const [fecha,          setFecha]          = useState(today());
  const [condPago,       setCondPago]       = useState('CONTADO');
  const [fechaVenc,      setFechaVenc]      = useState('');
  const [observacion,    setObservacion]    = useState('');
  const [items,          setItems]          = useState([newItem()]);
  const [prodSearch,     setProdSearch]     = useState({});
  const [prodSugest,     setProdSugest]     = useState({});
  const provRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/compras', {
        params: { q: search, from_date: fDesde, to_date: fHasta, estado: fEstado, proveedor_id: fProv }
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [search, fDesde, fHasta, fEstado, fProv]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    api.get('/api/proveedores').then(r => setProveedores(r.data.data || []));
  }, []);

  /* ── Buscar proveedor en el modal ── */
  const handleProvSearch = (v) => {
    setProvSearch(v);
    setProvSel(null);
    if (!v.trim()) { setProvSugest([]); return; }
    const lower = v.toLowerCase();
    setProvSugest(
      proveedores.filter(p =>
        p.nombre.toLowerCase().includes(lower) || (p.ruc || '').includes(lower)
      ).slice(0, 8)
    );
  };
  const selectProv = (p) => {
    setProvSel(p);
    setProvSearch(`${p.nombre}${p.ruc ? ` (${p.ruc})` : ''}`);
    setProvSugest([]);
  };

  /* ── Buscar producto en línea de detalle ── */
  const handleProdSearch = async (rowId, v) => {
    setProdSearch(prev => ({ ...prev, [rowId]: v }));
    updateItem(rowId, 'producto_nombre', v);
    updateItem(rowId, 'producto_id', null);
    if (!v.trim()) { setProdSugest(prev => ({ ...prev, [rowId]: [] })); return; }
    try {
      const r = await api.get('/api/productos', { params: { q: v } });
      const list = (r.data?.data || []).slice(0, 6);
      setProdSugest(prev => ({ ...prev, [rowId]: list }));
    } catch {
      setProdSugest(prev => ({ ...prev, [rowId]: [] }));
    }
  };
  const selectProd = (rowId, p) => {
    const unidadCompra = p.unidad_compra_short || p.unidad_compra_nombre || '';
    updateItem(rowId, 'producto_id', p.id);
    updateItem(rowId, 'producto_nombre', p.nombre || p.name);
    updateItem(rowId, 'unidad', unidadCompra);
    updateItem(rowId, 'precio_unitario', p.precio_compra || '');
    setProdSearch(prev => ({ ...prev, [rowId]: p.nombre || p.name }));
    setProdSugest(prev => ({ ...prev, [rowId]: [] }));
    recalcItem(rowId, {
      precio_unitario: p.precio_compra || '',
      producto_nombre: p.nombre || p.name,
      unidad: unidadCompra,
      producto_id: p.id,
    });
  };

  const updateItem = (rowId, field, value) => {
    setItems(prev => prev.map(it => it._id === rowId ? { ...it, [field]: value } : it));
  };
  const recalcItem = (rowId, extra = {}) => {
    setItems(prev => prev.map(it => {
      if (it._id !== rowId) return it;
      const merged = { ...it, ...extra };
      return { ...merged, subtotal: calcItem(merged) };
    }));
  };
  const handleItemChange = (rowId, field, value) => {
    setItems(prev => prev.map(it => {
      if (it._id !== rowId) return it;
      const updated = { ...it, [field]: value };
      return { ...updated, subtotal: calcItem(updated) };
    }));
  };
  const addRow = () => setItems(prev => [...prev, newItem()]);
  const removeRow = (rowId) => setItems(prev => prev.filter(it => it._id !== rowId));

  const totales = items.reduce((acc, it) => {
    acc.sub    += parseFloat(it.cantidad || 0) * parseFloat(it.precio_unitario || 0);
    acc.desc   += parseFloat(it.descuento || 0);
    acc.igv    += parseFloat(it.igv || 0);
    acc.total  += parseFloat(it.subtotal || 0);
    return acc;
  }, { sub: 0, desc: 0, igv: 0, total: 0 });

  const resetForm = () => {
    setProvSel(null); setProvSearch(''); setProvSugest([]);
    setTipoDoc('FACTURA'); setSerie(''); setNroDoc('');
    setFecha(today()); setCondPago('CONTADO'); setFechaVenc('');
    setObservacion('');
    setItems([newItem()]);
    setProdSearch({}); setProdSugest({});
    setModalError('');
  };

  const handleGuardar = async () => {
    if (!provSel) { setModalError('Seleccione un proveedor'); return; }
    const validItems = items.filter(it => it.producto_id && parseFloat(it.cantidad) > 0);
    if (!validItems.length) { setModalError('Seleccione al menos un producto vinculado con cantidad > 0'); return; }
    const unlinked = items.filter(it => it.producto_nombre.trim() && !it.producto_id);
    if (unlinked.length) { setModalError('Hay productos sin vincular. Selecciónelos desde la lista de búsqueda.'); return; }
    setSaving(true); setModalError('');
    try {
      await api.post('/api/compras', {
        proveedor_id:     provSel.id,
        tipo_documento:   tipoDoc,
        serie:            serie || null,
        nro_documento:    nroDoc || null,
        fecha,
        condicion_pago:   condPago,
        fecha_vencimiento: condPago === 'CREDITO' ? (fechaVenc || null) : null,
        subtotal:  parseFloat(totales.sub.toFixed(4)),
        descuento: parseFloat(totales.desc.toFixed(4)),
        igv:       parseFloat(totales.igv.toFixed(4)),
        total:     parseFloat(totales.total.toFixed(4)),
        observacion: observacion || null,
        items: validItems.map(it => ({
          producto_id:     it.producto_id || null,
          producto_nombre: it.producto_nombre,
          unidad:          it.unidad || '',
          cantidad:        parseFloat(it.cantidad),
          precio_unitario: parseFloat(it.precio_unitario),
          descuento:       parseFloat(it.descuento || 0),
          igv:             parseFloat(it.igv || 0),
          subtotal:        parseFloat(it.subtotal),
        })),
      });
      setModalNueva(false);
      resetForm();
      loadList();
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAnular = async () => {
    try {
      await api.patch(`/api/compras/${confirmId}/anular`);
      setConfirmId(null);
      loadList();
    } catch (e) {
      showToast(e.response?.data?.error || e.message);
    }
  };

  const openDetalle = async (id) => {
    try {
      const r = await api.get(`/api/compras/${id}`);
      setModalDet(r.data.data);
    } catch (e) {
      showToast(e.response?.data?.error || e.message);
    }
  };

  /* ── render ── */
  return (
    <div className="cmp-page">

      {/* toolbar */}
      <div className="cmp-toolbar">
        <button className="cmp-btn-nuevo" onClick={() => { resetForm(); setModalNueva(true); }}>
          <FiPlus size={14} /> Nueva Compra
        </button>

        <div className="cmp-search-box">
          <FiSearch size={13} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="N° compra, doc., proveedor…"
          />
        </div>

        <input type="date" className="cmp-filter-date" value={fDesde} onChange={e => setFDesde(e.target.value)} />
        <span className="cmp-filter-sep">—</span>
        <input type="date" className="cmp-filter-date" value={fHasta} onChange={e => setFHasta(e.target.value)} />

        <select className="cmp-filter-select" value={fEstado} onChange={e => setFEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="ANULADA">Anulada</option>
        </select>

        <select className="cmp-filter-select" value={fProv} onChange={e => setFProv(e.target.value)}>
          <option value="">Todos los proveedores</option>
          {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>

        {(search || fDesde || fHasta || fEstado || fProv) && (
          <button className="cmp-btn-clear" onClick={() => {
            setSearch(''); setFDesde(''); setFHasta(''); setFEstado(''); setFProv('');
          }}>
            <FiX size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* tabla */}
      <div className="cmp-table-wrap">
        {loading ? (
          <div className="cmp-empty">Cargando…</div>
        ) : error ? (
          <div className="cmp-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="cmp-empty">No hay compras registradas</div>
        ) : (
          <table className="cmp-table">
            <thead>
              <tr>
                <th>N° Compra</th>
                <th>Fecha</th>
                <th>Proveedor</th>
                <th>Tipo Doc.</th>
                <th>N° Documento</th>
                <th>Condición</th>
                <th>Venc.</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className={r.estado === 'ANULADA' ? 'anulada' : ''}>
                  <td><strong>{r.numero}</strong></td>
                  <td>{fmtFecha(r.fecha)}</td>
                  <td>
                    <div>{r.proveedor_nombre}</div>
                    {r.proveedor_ruc && <small style={{ color: '#64748b' }}>{r.proveedor_ruc}</small>}
                  </td>
                  <td>{r.tipo_documento}</td>
                  <td>{[r.serie, r.nro_documento].filter(Boolean).join('-') || '—'}</td>
                  <td>
                    <span className={`cmp-badge ${r.condicion_pago === 'CONTADO' ? 'contado' : 'credito'}`}>
                      {r.condicion_pago}
                    </span>
                  </td>
                  <td>{fmtFecha(r.fecha_vencimiento)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>S/ {fmt(r.total)}</td>
                  <td>
                    <span className={`cmp-badge ${r.estado === 'PENDIENTE' ? 'pendiente' : 'anulada'}`}>
                      {r.estado}
                    </span>
                  </td>
                  <td>
                    <div className="ia-actions">
                      <button className="ia-btn ia-btn--view" onClick={() => openDetalle(r.id)} title="Ver detalle">
                        <FiEye size={15} />
                      </button>
                      {r.estado === 'PENDIENTE' && (
                        <button className="ia-btn ia-btn--danger" onClick={() => setConfirmId(r.id)} title="Anular">
                          <FiXCircle size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* barra de totales */}
      <div className="cmp-total-bar">
        <span>{total} compra{total !== 1 ? 's' : ''}</span>
        <span>Mostrando: <strong>{rows.length}</strong></span>
      </div>

      {/* ── MODAL NUEVA COMPRA ── */}
      {modalNueva && (
        <div className="cmp-overlay">
          <div className="cmp-modal lg">
            <div className="cmp-modal-head">
              <h3>Nueva Compra</h3>
              <button className="cmp-modal-close" onClick={() => setModalNueva(false)}>×</button>
            </div>
            <div className="cmp-modal-body">
              {modalError && <div className="cmp-error">{modalError}</div>}

              <div className="cmp-form-grid">
                {/* Proveedor */}
                <div className="cmp-field cmp-form-col-full">
                  <label>Proveedor *</label>
                  <div className="cmp-search-dropdown" ref={provRef}>
                    <input
                      value={provSearch}
                      onChange={e => handleProvSearch(e.target.value)}
                      placeholder="Buscar por nombre o RUC…"
                    />
                    {provSugest.length > 0 && (
                      <div className="cmp-dropdown-list">
                        {provSugest.map(p => (
                          <div key={p.id} className="cmp-dropdown-item" onClick={() => selectProv(p)}>
                            {p.nombre} {p.ruc && <small>— {p.ruc}</small>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {provSel && (
                    <small style={{ color: '#15803d', marginTop: 2 }}>
                      ✓ {provSel.nombre}{provSel.ruc ? ` — RUC: ${provSel.ruc}` : ''}
                    </small>
                  )}
                </div>

                {/* Tipo documento */}
                <div className="cmp-field">
                  <label>Tipo Documento</label>
                  <select value={tipoDoc} onChange={e => setTipoDoc(e.target.value)}>
                    <option value="FACTURA">FACTURA</option>
                    <option value="BOLETA">BOLETA</option>
                    <option value="NOTA">NOTA</option>
                    <option value="NINGUNO">NINGUNO</option>
                  </select>
                </div>

                {/* Serie */}
                <div className="cmp-field">
                  <label>Serie</label>
                  <input value={serie} onChange={e => setSerie(e.target.value)} placeholder="F001" />
                </div>

                {/* N° Documento */}
                <div className="cmp-field">
                  <label>N° Documento</label>
                  <input value={nroDoc} onChange={e => setNroDoc(e.target.value)} placeholder="000001" />
                </div>

                {/* Fecha */}
                <div className="cmp-field">
                  <label>Fecha *</label>
                  <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
                </div>

                {/* Condición de pago */}
                <div className="cmp-field">
                  <label>Condición de Pago</label>
                  <select value={condPago} onChange={e => setCondPago(e.target.value)}>
                    <option value="CONTADO">CONTADO</option>
                    <option value="CREDITO">CRÉDITO</option>
                  </select>
                </div>

                {/* Fecha vencimiento (solo crédito) */}
                {condPago === 'CREDITO' && (
                  <div className="cmp-field">
                    <label>Fecha Vencimiento</label>
                    <input type="date" value={fechaVenc} onChange={e => setFechaVenc(e.target.value)} />
                  </div>
                )}

                {/* Observación */}
                <div className="cmp-field cmp-form-col-full">
                  <label>Observación</label>
                  <textarea
                    rows={2}
                    value={observacion}
                    onChange={e => setObservacion(e.target.value)}
                    placeholder="Opcional…"
                  />
                </div>
              </div>

              {/* ── DETALLE DE PRODUCTOS ── */}
              <div className="cmp-detalle-section">
                <h4>Detalle de productos</h4>
                <table className="cmp-detalle-table">
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Producto</th>
                      <th style={{ width: '10%' }}>Unidad</th>
                      <th style={{ width: '10%' }}>Cantidad</th>
                      <th style={{ width: '13%' }}>Precio Unit.</th>
                      <th style={{ width: '10%' }}>Descuento</th>
                      <th style={{ width: '10%' }}>IGV</th>
                      <th style={{ width: '10%', textAlign: 'right' }}>Subtotal</th>
                      <th style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it._id}>
                        {/* Producto search */}
                        <td style={{ position: 'relative' }}>
                          <input
                            value={prodSearch[it._id] ?? it.producto_nombre}
                            onChange={e => handleProdSearch(it._id, e.target.value)}
                            placeholder="Buscar o escribir…"
                          />
                          {(prodSugest[it._id] || []).length > 0 && (
                            <div className="cmp-dropdown-list" style={{ zIndex: 200 }}>
                              {prodSugest[it._id].map(p => (
                                <div
                                  key={p.id}
                                  className="cmp-dropdown-item"
                                  onClick={() => selectProd(it._id, p)}
                                >
                                  {p.nombre} <small>— {p.unidad_minima}</small>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {it.producto_id
                            ? <span className="cmp-und-badge">{it.unidad || '—'}</span>
                            : <input
                                value={it.unidad}
                                onChange={e => handleItemChange(it._id, 'unidad', e.target.value)}
                                placeholder="UND"
                              />
                          }
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="0.01"
                            value={it.cantidad}
                            onChange={e => handleItemChange(it._id, 'cantidad', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="0.01"
                            value={it.precio_unitario}
                            onChange={e => handleItemChange(it._id, 'precio_unitario', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="0.01"
                            value={it.descuento}
                            onChange={e => handleItemChange(it._id, 'descuento', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="0.01"
                            value={it.igv}
                            onChange={e => handleItemChange(it._id, 'igv', e.target.value)}
                          />
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          S/ {fmt(it.subtotal)}
                        </td>
                        <td>
                          <button
                            className="cmp-btn-del-row"
                            onClick={() => removeRow(it._id)}
                            disabled={items.length === 1}
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <button className="cmp-btn-add-row" onClick={addRow}>
                  + Agregar producto
                </button>

                {/* Totales */}
                <div className="cmp-form-totales">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <strong>S/ {fmt(totales.sub)}</strong>
                  </div>
                  {totales.desc > 0 && (
                    <div className="total-row">
                      <span>Descuento:</span>
                      <strong>— S/ {fmt(totales.desc)}</strong>
                    </div>
                  )}
                  {totales.igv > 0 && (
                    <div className="total-row">
                      <span>IGV:</span>
                      <strong>S/ {fmt(totales.igv)}</strong>
                    </div>
                  )}
                  <div className="total-row grand">
                    <span>TOTAL:</span>
                    <strong>S/ {fmt(totales.total)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="cmp-modal-foot">
              <button className="cmp-btn-cancel" onClick={() => setModalNueva(false)}>
                Cancelar
              </button>
              <button className="cmp-btn-save" onClick={handleGuardar} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar Compra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE ── */}
      {modalDet && (
        <div className="cmp-overlay">
          <div className="cmp-modal">
            <div className="cmp-modal-head">
              <h3>Compra {modalDet.numero}</h3>
              <button className="cmp-modal-close" onClick={() => setModalDet(null)}>×</button>
            </div>
            <div className="cmp-modal-body">
              <div className="cmp-detail-section">
                <h4>Información general</h4>
                <div className="cmp-detail-grid">
                  <span className="key">Proveedor</span>
                  <span className="val">{modalDet.proveedor_nombre} {modalDet.proveedor_ruc ? `— ${modalDet.proveedor_ruc}` : ''}</span>
                  <span className="key">Fecha</span>
                  <span className="val">{fmtFecha(modalDet.fecha)}</span>
                  <span className="key">Tipo Doc.</span>
                  <span className="val">{modalDet.tipo_documento}</span>
                  <span className="key">N° Documento</span>
                  <span className="val">{[modalDet.serie, modalDet.nro_documento].filter(Boolean).join('-') || '—'}</span>
                  <span className="key">Condición</span>
                  <span className="val">{modalDet.condicion_pago}</span>
                  <span className="key">Vencimiento</span>
                  <span className="val">{fmtFecha(modalDet.fecha_vencimiento)}</span>
                  <span className="key">Estado</span>
                  <span className="val">
                    <span className={`cmp-badge ${modalDet.estado === 'PENDIENTE' ? 'pendiente' : 'anulada'}`}>
                      {modalDet.estado}
                    </span>
                  </span>
                  {modalDet.observacion && <>
                    <span className="key">Observación</span>
                    <span className="val">{modalDet.observacion}</span>
                  </>}
                </div>
              </div>

              <div className="cmp-detail-section">
                <h4>Productos</h4>
                <table className="cmp-detalle-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Unidad</th>
                      <th style={{ textAlign: 'right' }}>Cantidad</th>
                      <th style={{ textAlign: 'right' }}>Precio Unit.</th>
                      <th style={{ textAlign: 'right' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(modalDet.detalle || []).map(d => (
                      <tr key={d.id}>
                        <td>{d.producto_nombre}</td>
                        <td>{d.unidad || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(d.cantidad)}</td>
                        <td style={{ textAlign: 'right' }}>S/ {fmt(d.precio_unitario)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>S/ {fmt(d.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="cmp-form-totales">
                <div className="total-row">
                  <span>Subtotal:</span><strong>S/ {fmt(modalDet.subtotal)}</strong>
                </div>
                {parseFloat(modalDet.descuento) > 0 && (
                  <div className="total-row">
                    <span>Descuento:</span><strong>— S/ {fmt(modalDet.descuento)}</strong>
                  </div>
                )}
                {parseFloat(modalDet.igv) > 0 && (
                  <div className="total-row">
                    <span>IGV:</span><strong>S/ {fmt(modalDet.igv)}</strong>
                  </div>
                )}
                <div className="total-row grand">
                  <span>TOTAL:</span><strong>S/ {fmt(modalDet.total)}</strong>
                </div>
              </div>

              {modalDet.cuenta_pagar && (
                <div className="cmp-detail-section" style={{ marginTop: 16 }}>
                  <h4>Cuenta por pagar</h4>
                  <div className="cmp-detail-grid">
                    <span className="key">Monto</span>
                    <span className="val">S/ {fmt(modalDet.cuenta_pagar.monto)}</span>
                    <span className="key">Pagado</span>
                    <span className="val">S/ {fmt(modalDet.cuenta_pagar.monto_pagado)}</span>
                    <span className="key">Restante</span>
                    <span className="val" style={{ color: '#ef4444', fontWeight: 700 }}>
                      S/ {fmt(parseFloat(modalDet.cuenta_pagar.monto) - parseFloat(modalDet.cuenta_pagar.monto_pagado))}
                    </span>
                    <span className="key">Estado</span>
                    <span className="val">
                      <span className={`cmp-badge cxp-${modalDet.cuenta_pagar.estado.toLowerCase()}`}>
                        {modalDet.cuenta_pagar.estado}
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="cmp-modal-foot">
              <button className="cmp-btn-cancel" onClick={() => setModalDet(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRMAR ANULACIÓN ── */}
      {confirmId && (
        <div className="cmp-overlay">
          <div className="cmp-modal sm">
            <div className="cmp-modal-head">
              <h3>Anular compra</h3>
              <button className="cmp-modal-close" onClick={() => setConfirmId(null)}>×</button>
            </div>
            <div className="cmp-modal-body">
              <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>
                ¿Confirma anular esta compra? Esta acción también anulará la cuenta por pagar asociada (si no está pagada). No se puede deshacer.
              </p>
            </div>
            <div className="cmp-modal-foot">
              <button className="cmp-btn-cancel" onClick={() => setConfirmId(null)}>Cancelar</button>
              <button className="cmp-btn-anular" onClick={handleAnular} style={{ padding: '8px 20px' }}>
                Sí, anular
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
